/** Recovery + running-state panel for the gap between paused stages.
 *
 * The HIL pipeline only mounts a StageReviewPanel when review_status starts
 * with "paused_at_". Between an approval and the next pause the job sits at
 * "in_progress" while the next agent runs on Kaycoo (synthesis stages
 * routinely take 60–120 s). Without this panel the page would look empty
 * and the reviewer would think the pipeline died.
 *
 * Two visual modes:
 *   - in_progress  -> subtle "Pipeline running" indicator, no actions.
 *   - other        -> amber "Pipeline stalled" panel with Resume button.
 *
 * The Resume button only fires when truly stalled (no in-flight agent).
 */
import { useEffect, useState } from 'react';
import { AlertCircle, RotateCw, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';

interface Props {
  jobId: string;
  reviewStatus: string | null;
  /** Job's top-level status. When 'cancelled', the panel renders as a
   *  Recall (different copy + colour) so the reviewer can re-open a
   *  cancelled pipeline without leaving the page. */
  jobStatus?: string | null;
  onResumed: () => void;
}

export function ResumePipelinePanel({ jobId, reviewStatus, jobStatus, onResumed }: Props) {
  const isRecall = jobStatus === 'cancelled';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After resume is clicked we suppress the stalled UI for a grace period so
  // the dispatched stage has time to come up before the reviewer sees the
  // amber panel again. Cleared by the parent re-rendering when the next stage
  // pauses (review_status switches to paused_at_*).
  const [graceUntil, setGraceUntil] = useState<number>(0);
  const inGrace = graceUntil > Date.now();
  const running = reviewStatus === 'in_progress';

  // While in grace, advance time by re-rendering once at expiry.
  useEffect(() => {
    if (!inGrace) return;
    const ms = graceUntil - Date.now();
    const t = setTimeout(() => { setGraceUntil(0); }, ms);
    return () => { clearTimeout(t); };
  }, [inGrace, graceUntil]);

  async function handleResume() {
    setSubmitting(true);
    setError(null);
    try {
      await api.post<{ resumed_at_stage: string }>(
        `/api/v1/jobs/${jobId}/stages/resume`,
        {},
      );
      // 90 s grace — enough for the slowest synthesis stage to either pause
      // or write an awaiting_review row of its own.
      setGraceUntil(Date.now() + 90_000);
      onResumed();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError ? err.message : 'Resume failed. Please retry.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (running || inGrace) {
    return (
      <Card className="border-border bg-muted/30">
        <CardContent className="pt-3 pb-3 px-4 flex items-center gap-2 text-sm flex-wrap">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-muted-foreground">
            Pipeline is running. Waiting for the next agent to finish and pause for review…
          </span>
          {/* Escape hatch: when the dispatcher silently dies (Railway
              cold-start, Kaycoo timeout, asyncio task crash), the job
              stays in 'in_progress' forever and the reviewer has no
              way out. This always-visible link re-dispatches the
              stage that should run next. Safe to click idempotently. */}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs ml-auto"
            onClick={() => { void handleResume(); }}
            disabled={submitting}
            title="Re-dispatch the next stage. Use this if the page has been showing 'running' for more than a minute."
          >
            Stuck? Kick the dispatcher →
          </Button>
        </CardContent>
        {error && (
          <CardContent className="pt-0 pb-3 px-4">
            <Alert variant="destructive">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>
    );
  }

  const palette = isRecall
    ? {
        card: 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30',
        icon: 'text-rose-700 dark:text-rose-300',
        title: 'text-rose-900 dark:text-rose-200',
        body: 'text-rose-800 dark:text-rose-300',
      }
    : {
        card: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30',
        icon: 'text-amber-700 dark:text-amber-300',
        title: 'text-amber-900 dark:text-amber-200',
        body: 'text-amber-800 dark:text-amber-300',
      };

  return (
    <Card className={palette.card}>
      <CardContent className="pt-4 pb-4 px-4 space-y-3">
        <div className="flex items-start gap-2">
          <AlertCircle className={`h-5 w-5 mt-0.5 shrink-0 ${palette.icon}`} aria-hidden="true" />
          <div>
            <p className={`text-sm font-medium ${palette.title}`}>
              {isRecall
                ? 'Pipeline cancelled — Recall to continue from the last approved stage'
                : 'Pipeline stalled — no stage is currently paused for review'}
            </p>
            <p className={`text-xs mt-1 ${palette.body}`}>
              {isRecall
                ? 'This contract was cancelled mid-pipeline, but earlier stages are already approved. Recall re-dispatches the stage that should run next from the approved history. No prior work is lost.'
                : (<>Current review status: <code>{reviewStatus ?? 'null'}</code>. An agent dispatch likely crashed before it could pause cleanly. Click Resume pipeline to figure out which stage should run next from the approved agent history and re-dispatch it.</>)}
            </p>
          </div>
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
        <Button onClick={() => { void handleResume(); }} disabled={submitting} className="gap-1.5">
          <RotateCw className="h-4 w-4" aria-hidden="true" />
          {isRecall ? 'Recall pipeline' : 'Resume pipeline'}
        </Button>
      </CardContent>
    </Card>
  );
}
